import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { SEOHelmet } from 'components/SEO';
import { UserIdentityBlock } from 'components/UserIdentityBlock';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import CommitmentConflictDialog from 'components/CommitmentConflictDialog/CommitmentConflictDialog';
import EditHaveListCardModal from 'components/EditHaveListCardModal/EditHaveListCardModal';
import EnableTradeOffersModal from 'components/EnableTradeOffersModal';
import { useGetPublicProfileQuery, useGetUserTradeStatsQuery } from 'store/services/profile/profileApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import {
  useCancelTradeOfferMutation,
  useCreateTradeOfferMutation,
  useLazyGetMyCommittedQuantitiesQuery,
} from 'store/services/tradeOffers/tradeOffersApi';
import {
  UpdateHaveListCardRequest,
  useUpdateHaveListCardMutation,
} from 'store/services/collectionList/haveList/haveListApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { MTGCard } from 'types/interfaces/mtgCard';
import { OfferLineItem } from 'types/interfaces/tradeOffer';
import { CommitmentConflict, findHaveIdConflicts } from 'utils/commitmentConflicts';
import { parseOfferError } from 'utils/offerErrors';
import { useWindowSize } from 'hooks/useWindowSize';
import { useCardSizePreference } from 'hooks/useCardSizePreference';
import CardSizeSelector from 'components/CardSizeSelector/CardSizeSelector';
import CardFilterRail from 'components/FilterPanel/CardFilterRail';
import { useLocalCardFilterState } from 'components/FilterPanel/cardFilterLocalState';
import { useDrawerLayout } from 'components/FilterPanel/useDrawerLayout';
import ChevronLeftIcon from 'components/Icons/ChevronLeftIcon';
import type { FinishCounts } from 'utils/finishFilter';
import {
  FocusSide,
  INITIAL_OFFER_FOCUS_STATE,
  offerFocusReducer,
  shouldAutoFocus,
} from 'utils/offerFocusState';
import { useSidebarCollapse } from 'context/SidebarCollapseContext';
import OfferSideStrip from './components/OfferSideStrip';
import { useRailOpenWhileFocused } from './hooks/useRailOpenWhileFocused';
import OfferPhoneFocusView from './components/OfferPhoneFocusView';
import type { SourceMode } from './components/SourcePanel';
import { OfferBuilderProvider, useOfferBuilder } from './context/OfferBuilderContext';
import SourcePanel from './components/SourcePanel';
import OfferColumn from './components/OfferColumn';
import OfferCommitBar from './components/OfferCommitBar';
import './OfferBuilderPage.css';

// Below this width four columns cannot breathe; collapse to the stepped flow
// over the SAME state — one data model, one propose handler, one payload shape.
const FOUR_COLUMN_MIN_WIDTH = 1100;

/**
 * Body class while the wide builder is mounted (Asana 1219902279124885
 * Decision 3). The page is the only scroller and the carts, strips and commit
 * bar pin with position: sticky; the class is what lets sticky engage inside
 * the app shell (see the rules in OfferBuilderPage.css).
 */
export const BUILDER_SCROLL_BODY_CLASS = 'offer-builder-page';

type BuilderStep = 1 | 2 | 3;

// EditHaveListCardModal is prop-driven on a have-list row; the modal only reads
// the identity/edit fields, so a view-model built from the line item suffices.
const toCollectionListRow = (item: OfferLineItem): CollectionList => ({
  id: item.userMTGCardHaveId,
  userId: 0,
  quantity: item.ownedQuantity,
  foil: item.foil,
  condition: item.condition as CardCondition,
  forTrade: true,
  createdAt: new Date(),
  card: {
    id: item.card.id,
    name: item.card.name,
    lang: item.card.lang,
    scryfallID: item.card.scryfallId || '',
    setCode: item.card.setCode,
    setName: item.card.setName,
    collectorNumber: item.card.collectorNumber,
    oracleId: item.card.oracleId,
  } as unknown as MTGCard,
});

// Half-banner avatars: both come from data the page already fetches
// (getMe for the viewer, getPublicProfile for the counterparty) — no new request.
interface BannerAvatar {
  avatarUrl: string | null;
  username?: string;
  /** Drives the flag badge. Both profile payloads carry it; passing it is the
   *  whole reason a flag renders here at all. */
  countryCode?: string | null;
  isElite: boolean;
  showAnimatedBorder: boolean;
  borderColor1: string | null;
  borderColor2: string | null;
  isLoading?: boolean;
}

interface BuilderContentProps {
  viewerPoints: number | null;
  counterpartyPoints: number | null;
  counterpartyRating: number | null;
  counterpartyRatingCount: number;
  counterpartyCardsSent: number | null;
  counterpartyIsElite: boolean;
  viewerAvatar: BannerAvatar;
  counterpartyAvatar: BannerAvatar;
}

/**
 * Load-path continuation: enabling the flag invalidates the PrivacyPreferences
 * tag and RTK Query refetches the candidates by itself, so there is nothing for
 * the continuation to do except let the dialog close. Module-level so its
 * identity is stable across renders.
 */
const noopContinuation = () => {};

const OfferBuilderContent: React.FC<BuilderContentProps> = ({
  viewerPoints,
  counterpartyPoints,
  counterpartyRating,
  counterpartyRatingCount,
  counterpartyCardsSent,
  counterpartyIsElite,
  viewerAvatar,
  counterpartyAvatar,
}) => {
  const navigate = useNavigate();
  const {
    counterpartyId,
    counterpartyUsername,
    youSend,
    theySend,
    suggestionId,
    clearAll,
    updateItemOwnedQuantity,
    setRejectedHaveId,
  } = useOfferBuilder();

  const { width } = useWindowSize();
  const isStepped = width > 0 && width < FOUR_COLUMN_MIN_WIDTH;
  const [step, setStep] = useState<BuilderStep>(1);
  const [cardSize, setCardSize] = useCardSizePreference('offerBuilder.cardSize');

  // ---- Focus mode (Asana 1219788320590626) ----
  // Page-held, never the URL and never the persisted builder store (Decision 1).
  const [focus, dispatchFocus] = useReducer(offerFocusReducer, INITIAL_OFFER_FOCUS_STATE);
  const focusSide = focus.focusSide;
  // One filter api per side, in memory for the session (Decision 6).
  const sendFilters = useLocalCardFilterState();
  const receiveFilters = useLocalCardFilterState();
  const filtersBySide: Record<FocusSide, typeof sendFilters> = {
    send: sendFilters,
    receive: receiveFilters,
  };
  // The grid opens at S (Decision 5), remembered separately from the row size.
  const [gridSize, setGridSize] = useCardSizePreference('offerBuilder.focusCardSize', undefined, 'S');
  const [modeBySide, setModeBySide] = useState<Record<FocusSide, SourceMode>>({
    send: 'intersection',
    receive: 'intersection',
  });
  const [finishCountsBySide, setFinishCountsBySide] = useState<
    Record<FocusSide, FinishCounts | undefined>
  >({ send: undefined, receive: undefined });
  const [totalCountBySide, setTotalCountBySide] = useState<Record<FocusSide, number | undefined>>({
    send: undefined,
    receive: undefined,
  });
  const onSendModeChange = useCallback(
    (mode: SourceMode) => setModeBySide((prev) => (prev.send === mode ? prev : { ...prev, send: mode })),
    []
  );
  const onReceiveModeChange = useCallback(
    (mode: SourceMode) =>
      setModeBySide((prev) => (prev.receive === mode ? prev : { ...prev, receive: mode })),
    []
  );
  const onSendResultMeta = useCallback(
    ({ finishCounts, totalCount }: { finishCounts?: FinishCounts; totalCount?: number }) => {
      setFinishCountsBySide((prev) =>
        prev.send === finishCounts ? prev : { ...prev, send: finishCounts }
      );
      setTotalCountBySide((prev) => (prev.send === totalCount ? prev : { ...prev, send: totalCount }));
    },
    []
  );
  const onReceiveResultMeta = useCallback(
    ({ finishCounts, totalCount }: { finishCounts?: FinishCounts; totalCount?: number }) => {
      setFinishCountsBySide((prev) =>
        prev.receive === finishCounts ? prev : { ...prev, receive: finishCounts }
      );
      setTotalCountBySide((prev) =>
        prev.receive === totalCount ? prev : { ...prev, receive: totalCount }
      );
    },
    []
  );
  const focusSend = useCallback(() => dispatchFocus({ type: 'focus', side: 'send' }), []);
  const focusReceive = useCallback(() => dispatchFocus({ type: 'focus', side: 'receive' }), []);
  // Show both sides / the strip: a decline is recorded only while that side is
  // on its full list (Decision 10).
  const unfocus = useCallback(() => {
    if (!focusSide) return;
    dispatchFocus({ type: 'unfocus', sideOnFullList: modeBySide[focusSide] === 'full' });
  }, [focusSide, modeBySide]);
  // The rail is one component fed by whichever side is focused (Decision 6);
  // it stays mounted and animates to width 0 through `hidden` (ruling 7).
  const layout = useDrawerLayout({ cartOpen: false, railMounted: !isStepped && focusSide !== null });
  // Entering focus by any route holds the rail open without writing the
  // stored preference; leaving focus releases it (QA round 1, Decision 1).
  useRailOpenWhileFocused(!isStepped && focusSide !== null);
  // LocalTextInput adopts an external value only while unfocused, so a rail
  // field still holding the caret would keep the other side's text.
  useEffect(() => {
    const el = document.activeElement;
    if (el instanceof HTMLElement && el.closest('.right-rail')) el.blur();
  }, [focusSide]);
  const railSide: FocusSide = focusSide ?? 'receive';
  const listNoun = (side: FocusSide) => (side === 'send' ? 'want list' : 'have list');

  // ---- Auto-focus and the nav (Decisions 9 to 11) ----
  // The nav fold rides the existing page-collapse request: it never touches
  // the stored preference, it is counted (StrictMode-safe), and a manual
  // expand while it is held is the context's veto, which is how "the user's
  // choice wins" is detected here. Decision 10's opt-out lives in the reducer.
  const { isCollapsed: navCollapsed, requestPageCollapse, releasePageCollapse } = useSidebarCollapse();
  const heldNavRequestRef = useRef(false);
  const releaseHeldNav = useCallback(() => {
    if (!heldNavRequestRef.current) return;
    heldNavRequestRef.current = false;
    releasePageCollapse();
  }, [releasePageCollapse]);

  const handleFullListSelected = useCallback(
    (side: FocusSide, fullListTotal: number | undefined) => {
      if (!shouldAutoFocus(focus, side, fullListTotal)) return;
      // Phones never fold the nav (Decision 14); a nav already collapsed by
      // the user's own preference is not ours to re-open.
      const navWasOpen = !isStepped && !navCollapsed;
      if (navWasOpen && !heldNavRequestRef.current) {
        heldNavRequestRef.current = true;
        requestPageCollapse();
      }
      dispatchFocus({ type: 'auto_focus', side, navWasOpen });
    },
    [focus, isStepped, navCollapsed, requestPageCollapse]
  );
  const onSendFullListSelected = useCallback(
    (total: number | undefined) => handleFullListSelected('send', total),
    [handleFullListSelected]
  );
  const onReceiveFullListSelected = useCallback(
    (total: number | undefined) => handleFullListSelected('receive', total),
    [handleFullListSelected]
  );

  // While the request is held the nav can only open through the user's veto.
  useEffect(() => {
    if (heldNavRequestRef.current && !navCollapsed) {
      dispatchFocus({ type: 'nav_toggled_manually' });
      releaseHeldNav();
    }
  }, [navCollapsed, releaseHeldNav]);

  // Back to four columns: re-open the nav only if the builder folded it and
  // the user did not toggle it in between (navAutoCollapsed still true).
  const navAutoCollapsed = focus.navAutoCollapsed;
  useEffect(() => {
    if (focusSide !== null) return;
    if (navAutoCollapsed) {
      releaseHeldNav();
      dispatchFocus({ type: 'nav_restored' });
    }
  }, [focusSide, navAutoCollapsed, releaseHeldNav]);

  // The context's pairing rule: never leave a request behind on unmount.
  useEffect(() => releaseHeldNav, [releaseHeldNav]);

  // One scrollbar: the page (QA round 1, Decision 3, replacing Decision 12).
  // The builder is content height; the carts, the folded strips and the commit
  // bar pin with position: sticky. Sticky cannot engage inside the shell as
  // shipped: body's overflow-x and the two Main.css wrappers are each a scroll
  // container that never scrolls (measured 2026-10-26, every variant), so while
  // the wide builder is mounted the body carries a class whose rules set the
  // three to overflow: visible, the way useDrawerLayout scopes body.right-drawers.
  const builderRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isStepped) return undefined;
    document.body.classList.add(BUILDER_SCROLL_BODY_CLASS);
    return () => document.body.classList.remove(BUILDER_SCROLL_BODY_CLASS);
  }, [isStepped]);

  // The pinned columns' ceiling is the viewport minus the commit bar, which
  // wraps to two rows at narrower widths, so its live height is published as
  // --offer-commit-h on the builder (53px fallback in the CSS). jsdom has no
  // ResizeObserver; the one-off write still runs there.
  useEffect(() => {
    const el = builderRef.current;
    if (!el || isStepped) return undefined;
    const bar = el.querySelector<HTMLElement>('.offer-commit-bar');
    if (!bar) return undefined;
    const write = () =>
      el.style.setProperty('--offer-commit-h', `${Math.round(bar.getBoundingClientRect().height)}px`);
    write();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(write);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [isStepped]);

  const [createOffer, { isLoading: isCreating }] = useCreateTradeOfferMutation();
  const [cancelOffer] = useCancelTradeOfferMutation();
  const [fetchCommitted, { isFetching: isCheckingCommitted }] =
    useLazyGetMyCommittedQuantitiesQuery();
  const [updateHaveListCard] = useUpdateHaveListCardMutation();

  const [conflicts, setConflicts] = useState<CommitmentConflict[]>([]);
  /**
   * Set when the server rejects with PROPOSER_OFFERS_DISABLED — the viewer has
   * turned off their own trade offers, which under the reciprocity rule also
   * stops them sending. The stored callback is the action to resume once the
   * flag is back on, so enabling continues what they were doing instead of
   * dropping them at square one. Two producers: the propose click below, and
   * the candidates fetch in SourcePanel (the builder can 403 on LOAD).
   */
  const [offersDisabledContinuation, setOffersDisabledContinuation] = useState<
    (() => void | Promise<void>) | null
  >(null);
  const [editTarget, setEditTarget] = useState<OfferLineItem | null>(null);
  const [isCancellingOffer, setIsCancellingOffer] = useState(false);

  /**
   * LOAD-path entry to the same dialog. The candidates fetch 403'd because the
   * viewer's own offers are off, so the continuation is "let the panels load",
   * NOT a proposal — there is nothing to propose yet. Enabling invalidates the
   * PrivacyPreferences tag; RTK Query refetches the candidates on its own, so
   * the continuation only has to close the dialog. The builder's sessionStorage
   * state is untouched throughout, so an in-progress offer survives.
   *
   * Stored as a no-op thunk rather than left null so the dialog's isOpen check
   * (`!== null`) stays a single rule for both entry points.
   */
  const handleProposerOffersDisabledOnLoad = React.useCallback(() => {
    // The functional updater form is what keeps this idempotent: the panel's
    // effect can fire on re-render, and it must not clobber a continuation the
    // propose path already stored. Note the double arrow — a function stored in
    // state has to be wrapped, or React would call it as an updater.
    setOffersDisabledContinuation((prev) => prev ?? noopContinuation);
  }, []);

  const doCreate = async () => {
    await createOffer({
      recipientId: counterpartyId,
      proposerItems: youSend.map((i) => ({
        userMTGCardHaveId: i.userMTGCardHaveId,
        quantity: i.quantity,
        // Omit-when-equal: an untouched selector produces today's payload
        // byte for byte; the server reads an absent condition as the row's.
        ...(i.declaredCondition && i.declaredCondition !== i.condition
          ? { condition: i.declaredCondition }
          : {}),
      })),
      recipientItems: theySend.map((i) => ({
        userMTGCardHaveId: i.userMTGCardHaveId,
        quantity: i.quantity,
      })),
      ...(suggestionId ? { suggestionId } : {}),
    }).unwrap();
    toast.success(`Trade offer sent to ${counterpartyUsername}`);
    clearAll();
    navigate('/trade-offers');
  };

  const handlePropose = async () => {
    try {
      // Fresh read every time: another tab or an accepted offer can change
      // commitments between clicks (preferCacheValue=false).
      const committed = await fetchCommitted(undefined, false).unwrap();
      const found = findHaveIdConflicts(
        youSend.map((i) => ({
          userMTGCardHaveId: i.userMTGCardHaveId,
          quantity: i.quantity,
          ownedQuantity: i.ownedQuantity,
          cardName: i.card.name,
        })),
        committed.byHaveId
      );
      if (found.length > 0) {
        setConflicts(found);
        return;
      }
      await doCreate();
    } catch (err) {
      const parsed = parseOfferError(err);
      if (parsed.kind === 'proposer_offers_disabled') {
        // Recoverable in place. Resume the PROPOSAL itself, not a reload: the
        // builder's state is intact, so the user lands back on the same offer.
        setOffersDisabledContinuation(() => doCreate);
        return;
      }
      if (parsed.kind === 'exceeds_want_quantity' && parsed.exceedsWant) {
        // Mark the offending line as well as toasting. The message names a
        // card, but the same card in three sets is three lines — only the
        // have-row id says WHICH one to remove. Unreachable through the UI
        // (the panel gates the add), so this covers a stale session, a want
        // the recipient shrank mid-build, or a client bypass.
        setRejectedHaveId(parsed.exceedsWant.userMTGCardHaveId);
      }
      toast.error(parsed.message);
    }
  };

  const handleCancelConflictingOffers = async () => {
    setIsCancellingOffer(true);
    try {
      const offerIds = Array.from(new Set(conflicts.flatMap((c) => c.offerIds)));
      for (const offerId of offerIds) {
        await cancelOffer(offerId).unwrap();
      }
      setConflicts([]);
      await handlePropose();
    } catch (err) {
      toast.error(parseOfferError(err).message);
    } finally {
      setIsCancellingOffer(false);
    }
  };

  const handleEditHaveList = () => {
    const target = conflicts
      .map((c) => youSend.find((i) => i.userMTGCardHaveId === c.userMTGCardHaveId))
      .find(Boolean);
    setConflicts([]);
    if (target) setEditTarget(target);
  };

  const handleEditSave = async (updates: UpdateHaveListCardRequest) => {
    await updateHaveListCard(updates).unwrap();
    if (editTarget && typeof updates.quantity === 'number') {
      updateItemOwnedQuantity('youSend', editTarget.userMTGCardHaveId, updates.quantity);
    }
  };

  const isProposing = isCreating || isCheckingCommitted;

  // The counterparty's full identity treatment, matching /sendcards: 48px avatar
  // with country flag and Elite border, username + Elite badge, point balance,
  // and the UserHoverCard overlay on hover. The rating line rides in the block's
  // one child slot so it reads as secondary to the identity above it.
  const trustLine = (
    <UserIdentityBlock
      username={counterpartyUsername}
      avatarUrl={counterpartyAvatar.avatarUrl}
      country={counterpartyAvatar.countryCode}
      points={counterpartyPoints}
      isElite={counterpartyIsElite}
      showAnimatedBorder={counterpartyAvatar.showAnimatedBorder}
      borderColor1={counterpartyAvatar.borderColor1}
      borderColor2={counterpartyAvatar.borderColor2}
      className="offer-builder-trust"
    >
      <span className="offer-builder-trust-stats">
        {counterpartyRating != null
          ? `★ ${counterpartyRating.toFixed(1)} (${counterpartyRatingCount})`
          : 'No ratings yet'}
        {counterpartyCardsSent != null && ` · ${counterpartyCardsSent.toLocaleString()} cards sent`}
      </span>
    </UserIdentityBlock>
  );

  const footer = (
    <OfferCommitBar
      viewerPoints={viewerPoints}
      counterpartyPoints={counterpartyPoints}
      isProposing={isProposing}
      onPropose={handlePropose}
    />
  );

  const youSendGuidance = {
    arrowDirection: 'left' as const,
    addLine: 'Add cards from the panel on the left.',
    relationLine: `Those are cards ${counterpartyUsername} wants that you already own.`,
  };
  const theySendGuidance = {
    arrowDirection: 'right' as const,
    addLine: 'Add cards from the panel on the right.',
    relationLine: `Those are cards ${counterpartyUsername} owns that are on your want list.`,
  };

  return (
    <div
      ref={builderRef}
      className={`offer-builder size-${cardSize.toLowerCase()}${
        !isStepped ? ` ${layout.pushProps.className}` : ''
      }`}
      data-focus={isStepped ? 'none' : focusSide ?? 'none'}
    >
      {!isStepped ? (
        <>
          {/* The page is the only scroller (QA round 1, Decision 3): the body
              is content height, the carts and strips are sticky beside the
              list, and the commit bar is sticky at the bottom. */}
            <header className="offer-builder-header">
              <h1 className="offer-builder-heading">Build a trade with {counterpartyUsername}</h1>
            {/* The only new element when a suggestion seeds the builder. Both
                browse panels, the steppers, the condition selector, the S/M/L
                control and the footer behave exactly as they do unseeded. */}
            {suggestionId && (
              <span className="offer-builder-seeded-chip">Seeded from a suggested trade</span>
            )}
              {trustLine}
              <CardSizeSelector
                value={cardSize}
                onChange={setCardSize}
                className="offer-builder-size"
              />
            </header>
          <div className="offer-builder-body">
          <OfferSideStrip
            side="send"
            folded={focusSide === 'receive'}
            avatar={viewerAvatar}
            counterpartyUsername={counterpartyUsername}
            cartCount={youSend.length}
            onClick={unfocus}
          />
          <div className="offer-builder-main">
            {/* The four mandated columns read as two halves: YOU ADD spans the
                left source + You send; THEY ADD spans They send + the right
                source. Each source sits adjacent to the column it fills — their
                want list holds cards YOU send; their have list holds cards THEY
                send. The participant avatars bookend the row (viewer far left,
                counterparty far right) and are decorative: names are stated
                elsewhere on the page, so they are hidden from screen readers. */}
            <div className="offer-half-headers">
              <div className="offer-half offer-half--yours">
                <button
                  type="button"
                  className="offer-half-fold"
                  onClick={focusReceive}
                  aria-label="Fold your side; browse their have list full width"
                  data-testid="offer-fold-send"
                >
                  <ChevronLeftIcon size={14} color="#ffffff" />
                </button>
                <span className="offer-half-avatar" aria-hidden="true">
                  <AvatarWithFlag
                    avatarUrl={viewerAvatar.avatarUrl}
                    username={viewerAvatar.username}
                    countryCode={viewerAvatar.countryCode}
                    size={24}
                    alt=""
                    isLoading={viewerAvatar.isLoading}
                    isElite={viewerAvatar.isElite}
                    showAnimatedBorder={viewerAvatar.showAnimatedBorder}
                    borderColor1={viewerAvatar.borderColor1}
                    borderColor2={viewerAvatar.borderColor2}
                  />
                </span>
                <span className="offer-half-title">YOU ADD</span>
                <span className="offer-half-sub">cards leaving your collection</span>
                <span className="offer-half-arrow" aria-hidden="true">
                  →
                </span>
              </div>
              <div className="offer-half offer-half--theirs">
                <span className="offer-half-arrow" aria-hidden="true">
                  ←
                </span>
                <span className="offer-half-title">THEY ADD</span>
                <span className="offer-half-sub">cards coming to you</span>
                <span className="offer-half-avatar" aria-hidden="true">
                  <AvatarWithFlag
                    avatarUrl={counterpartyAvatar.avatarUrl}
                    username={counterpartyAvatar.username}
                    countryCode={counterpartyAvatar.countryCode}
                    size={24}
                    alt=""
                    isElite={counterpartyAvatar.isElite}
                    showAnimatedBorder={counterpartyAvatar.showAnimatedBorder}
                    borderColor1={counterpartyAvatar.borderColor1}
                    borderColor2={counterpartyAvatar.borderColor2}
                  />
                </span>
                <button
                  type="button"
                  className="offer-half-fold offer-half-fold--right"
                  onClick={focusSend}
                  aria-label="Fold their side; browse their want list full width"
                  data-testid="offer-fold-receive"
                >
                  <ChevronLeftIcon size={14} color="#ffffff" />
                </button>
              </div>
            </div>
            {/* Flex, not grid: the focus mode animates these tracks between
                their four-column and folded sizes, and flex-basis/flex-grow
                interpolate where grid tracks do not. */}
            <div className="offer-builder-grid">
              <div
                className="offer-col offer-col--src offer-col--src-send"
                inert={focusSide === 'receive' || undefined}
              >
                <SourcePanel
                  side="send"
                  focused={focusSide === 'send'}
                  filters={sendFilters}
                  gridSize={gridSize}
                  onGridSizeChange={setGridSize}
                  onExpand={focusSend}
                  onShowBoth={unfocus}
                  onFullListSelected={onSendFullListSelected}
                  onModeChange={onSendModeChange}
                  onResultMeta={onSendResultMeta}
                />
              </div>
              <div
                className="offer-col offer-col--cart offer-col--cart-you"
                inert={focusSide === 'receive' || undefined}
              >
                <OfferColumn side="youSend" title="You send" emptyGuidance={youSendGuidance} />
              </div>
              <div
                className="offer-col offer-col--cart offer-col--cart-they"
                inert={focusSide === 'send' || undefined}
              >
                <OfferColumn side="theySend" title="They send" emptyGuidance={theySendGuidance} />
              </div>
              <div
                className="offer-col offer-col--src offer-col--src-receive"
                inert={focusSide === 'send' || undefined}
              >
                <SourcePanel
                  side="receive"
                  onProposerOffersDisabled={handleProposerOffersDisabledOnLoad}
                  focused={focusSide === 'receive'}
                  filters={receiveFilters}
                  gridSize={gridSize}
                  onGridSizeChange={setGridSize}
                  onExpand={focusReceive}
                  onShowBoth={unfocus}
                  onFullListSelected={onReceiveFullListSelected}
                  onModeChange={onReceiveModeChange}
                  onResultMeta={onReceiveResultMeta}
                />
              </div>
            </div>
          </div>
          <OfferSideStrip
            side="receive"
            folded={focusSide === 'send'}
            avatar={counterpartyAvatar}
            counterpartyUsername={counterpartyUsername}
            cartCount={theySend.length}
            onClick={unfocus}
          />
          </div>
          {footer}
          {/* Mounted whenever wide and hidden (width 0) outside focus mode, so
              the column animates on the shared drawer timing (ruling 7). */}
          <CardFilterRail
            filters={filtersBySide[railSide]}
            hidden={focusSide === null}
            hideTradeSection
            subtitle={
              focusSide ? (
                <>
                  Filtering <strong>{counterpartyUsername}&apos;s {listNoun(focusSide)}</strong>
                </>
              ) : undefined
            }
            showFinish
            finishCounts={focusSide ? finishCountsBySide[focusSide] : undefined}
          />
        </>
      ) : (
        <div className={`offer-builder-stepped${focusSide ? ' offer-builder-stepped--focused' : ''}`}>
          {focusSide ? (
            // Decision 14: the full-screen grid replaces the title, the step
            // pills and the step's panel + column. Next leaves the grid and
            // advances the step; the back chevron is Show both sides.
            <OfferPhoneFocusView
              side={focusSide}
              counterpartyUsername={counterpartyUsername}
              filters={filtersBySide[focusSide]}
              finishCounts={finishCountsBySide[focusSide]}
              totalCount={totalCountBySide[focusSide]}
              gridSize={gridSize}
              onGridSizeChange={setGridSize}
              cardSize={cardSize}
              emptyGuidance={focusSide === 'send' ? youSendGuidance : theySendGuidance}
              nextLabel={focusSide === 'send' ? 'Next: pick what they send' : 'Next: review'}
              onBack={unfocus}
              onNext={() => {
                unfocus();
                setStep(focusSide === 'send' ? 2 : 3);
              }}
              onModeChange={focusSide === 'send' ? onSendModeChange : onReceiveModeChange}
              onResultMeta={focusSide === 'send' ? onSendResultMeta : onReceiveResultMeta}
              initialMode={modeBySide[focusSide]}
              onProposerOffersDisabled={
                focusSide === 'receive' ? handleProposerOffersDisabledOnLoad : undefined
              }
            />
          ) : (
          <>
          <header className="offer-builder-header">
            <h1 className="offer-builder-heading">Build a trade with {counterpartyUsername}</h1>
            {/* The only new element when a suggestion seeds the builder. Both
                browse panels, the steppers, the condition selector, the S/M/L
                control and the footer behave exactly as they do unseeded. */}
            {suggestionId && (
              <span className="offer-builder-seeded-chip">Seeded from a suggested trade</span>
            )}
            {trustLine}
            <CardSizeSelector
              value={cardSize}
              onChange={setCardSize}
              className="offer-builder-size"
            />
          </header>
          <div className="offer-builder-steps-nav" role="tablist" aria-label="Builder steps">
            {[1, 2, 3].map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={step === s}
                className={`offer-step-pill${step === s ? ' active' : ''}`}
                onClick={() => setStep(s as BuilderStep)}
              >
                {s === 1 ? '1. You send' : s === 2 ? '2. They send' : '3. Review'}
              </button>
            ))}
          </div>
          {step === 1 && (
            <>
              <SourcePanel
                side="send"
                phone
                filters={sendFilters}
                onFullListSelected={onSendFullListSelected}
                onModeChange={onSendModeChange}
                initialMode={modeBySide.send}
              />
              <OfferColumn side="youSend" title="You send" emptyGuidance={youSendGuidance} />
              <button type="button" className="offer-step-next" onClick={() => setStep(2)}>
                Next: pick what they send
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <SourcePanel
                side="receive"
                onProposerOffersDisabled={handleProposerOffersDisabledOnLoad}
                phone
                filters={receiveFilters}
                onFullListSelected={onReceiveFullListSelected}
                onModeChange={onReceiveModeChange}
                initialMode={modeBySide.receive}
              />
              <OfferColumn side="theySend" title="They send" emptyGuidance={theySendGuidance} />
              <button type="button" className="offer-step-next" onClick={() => setStep(3)}>
                Next: review and propose
              </button>
            </>
          )}
          {step === 3 && (
            <>
              <OfferColumn side="youSend" title="You send" emptyGuidance={youSendGuidance} />
              <OfferColumn side="theySend" title="They send" emptyGuidance={theySendGuidance} />
              {footer}
            </>
          )}
          </>
          )}
        </div>
      )}

      <EnableTradeOffersModal
        isOpen={offersDisabledContinuation !== null}
        onClose={() => setOffersDisabledContinuation(null)}
        onContinue={() => {
          const resume = offersDisabledContinuation;
          setOffersDisabledContinuation(null);
          return resume?.();
        }}
      />
      <CommitmentConflictDialog
        isOpen={conflicts.length > 0}
        conflicts={conflicts}
        isCancellingOffer={isCancellingOffer}
        onAbort={() => setConflicts([])}
        onCancelConflictingOffers={handleCancelConflictingOffers}
        onEditHaveList={handleEditHaveList}
      />
      <EditHaveListCardModal
        isOpen={editTarget !== null}
        card={editTarget ? toCollectionListRow(editTarget) : null}
        onClose={() => setEditTarget(null)}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default function OfferBuilderPage() {
  const { username } = useParams<{ username: string }>();
  const { data: me, isLoading: isMeLoading } = useGetMeQuery();
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useGetPublicProfileQuery(username || '', { skip: !username });
  const { data: stats } = useGetUserTradeStatsQuery(username || '', { skip: !username });

  const isSelf = useMemo(
    () => !!me && !!profile && me.id === profile.id,
    [me, profile]
  );

  return (
    <>
      <SEOHelmet
        title={`Build a Trade Offer${username ? ` with ${username}` : ''} - TradeMagic`}
        description="Compose a two-sided trade offer: pick cards from their want list and have list, see both totals, and propose with prices locked."
      />
      {isProfileLoading ? (
        <div className="offer-builder-status">Loading trader…</div>
      ) : profileError || !profile ? (
        <div className="offer-builder-status">
          This trader could not be found. Check the link and try again.
        </div>
      ) : isSelf ? (
        <div className="offer-builder-status">You can&apos;t build a trade offer with yourself.</div>
      ) : (
        <OfferBuilderProvider
          counterpartyId={profile.id}
          counterpartyUsername={profile.username}
          viewerPoints={me?.points ?? null}
          counterpartyPoints={profile.points ?? null}
        >
          <OfferBuilderContent
            viewerPoints={me?.points ?? null}
            counterpartyPoints={profile.points ?? null}
            counterpartyRating={profile.averageRating}
            counterpartyRatingCount={profile.totalRatings}
            counterpartyCardsSent={stats?.cardsSent ?? null}
            counterpartyIsElite={!!profile.isElite}
            viewerAvatar={{
              avatarUrl: me?.avatarUrl ?? null,
              username: me?.username,
              countryCode: me?.country ?? null,
              isElite: !!me?.isElite,
              showAnimatedBorder: !!me?.showAnimatedBorder,
              borderColor1: me?.borderColor1 ?? null,
              borderColor2: me?.borderColor2 ?? null,
              isLoading: isMeLoading,
            }}
            counterpartyAvatar={{
              avatarUrl: profile.avatarUrl,
              username: profile.username,
              countryCode: profile.country,
              isElite: !!profile.isElite,
              showAnimatedBorder: !!profile.showAnimatedBorder,
              borderColor1: profile.borderColor1 ?? null,
              borderColor2: profile.borderColor2 ?? null,
            }}
          />
        </OfferBuilderProvider>
      )}
    </>
  );
}
